import { RequestPayload, router } from '@inertiajs/core';
import { usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { Calendar, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { SharedProps } from '../types';
import { Button } from './Button';
import { DateInput } from './DateInput';
import { Icon } from './Icon';
import { InputWrapper } from './InputWrapper';

interface DateFilterInputProps {
  /**
   * If a name is given, it will be used to prefix the URL param.
   * Use if multiple date inputs are on the same page.
   */
  name?: string;
  pageParam?: string;
  id?: string;
  startDate?: string;
  endDate?: string;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  onChange?: (value: string | null) => void;
  payload?: (value: string | null) => RequestPayload;
}

export function DateFilterInput({
  name = 'date',
  pageParam = 'page',
  id = 'date_filter',
  startDate,
  endDate,
  selectsStart,
  selectsEnd,
  onChange,
  payload = () => ({ page: undefined }),
}: DateFilterInputProps) {
  const { t } = useTranslation();
  const { params } = usePage<SharedProps>().props;
  const value = params[name] as string || '';

  const handleChange = (value: string | null) => {
    router.get(
      '',
      {
        [name]: value,
        ...payload(value),
      },
      { preserveScroll: true, preserveState: true, replace: true },
    );

    onChange?.(value);
  };

  const handleReset = () => {
    router.get(
      '',
      { [name]: undefined, [pageParam]: undefined },
      { preserveScroll: true, preserveState: true, replace: true },
    );
  };

  return (
    <InputWrapper>
      <label htmlFor={id}>
        <Icon className={classNames(value ? 'text-slate-800' : 'text-slate-400')}>
          <Calendar />
        </Icon>
      </label>
      <DateInput
        value={value}
        onChange={handleChange}
        startDate={startDate}
        endDate={endDate}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        id={id}
      />
      {value && (
        <Button
          onClick={handleReset}
          variant="tertiary"
          className="relative text-slate-400 hover:text-slate-800 !border-transparent"
          aria-label={t('shared:filter.clear')}
        >
          <Icon>
            <XCircle />
          </Icon>
        </Button>
      )}
    </InputWrapper>
  );
}
